import clsx from 'clsx';
import type { ChangeEvent, JSX } from 'react';
import {
  PUBLIC_ASSET_TYPE_LABELS,
  PUBLIC_READINESS_LABELS,
  PUBLIC_READINESS_ORDER,
} from '@/components/public/properties';
import type {
  DashboardPropertyFilterUpdater,
  DashboardPropertyFiltersState,
} from '@/hooks/useDashboardPropertyFilters';
import type { AssetType, UnitAvailabilityStatus } from '@/lib/types/property';

export interface DashboardPropertyFiltersProps {
  filters: DashboardPropertyFiltersState;
  onFiltersChange: (updater: DashboardPropertyFilterUpdater) => void;
  onReset: () => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  submarketOptions: string[];
  assetTypeOptions?: AssetType[];
  showHeader?: boolean;
}

export function DashboardPropertyFilters({
  filters,
  onFiltersChange,
  onReset,
  activeFilterCount,
  hasActiveFilters,
  submarketOptions,
  assetTypeOptions = Object.keys(PUBLIC_ASSET_TYPE_LABELS) as AssetType[],
  showHeader = true,
}: DashboardPropertyFiltersProps): JSX.Element {
  const normalizedSubmarkets = Array.from(
    new Set([
      ...submarketOptions.map((entry) => entry.trim()).filter((entry) => entry.length > 0),
      ...filters.submarkets,
    ]),
  ).sort((a, b) => a.localeCompare(b));

  const handleSubmarketToggle = (value: string): void => {
    onFiltersChange((current) => ({
      ...current,
      submarkets: toggleString(current.submarkets, value),
    }));
  };

  const handleAssetTypeToggle = (value: AssetType): void => {
    onFiltersChange((current) => ({
      ...current,
      assetTypes: toggleEnum(current.assetTypes, value),
    }));
  };

  const handleReadinessToggle = (value: UnitAvailabilityStatus): void => {
    onFiltersChange((current) => ({
      ...current,
      readinessStates: toggleEnum(current.readinessStates, value),
    }));
  };

  const handleRangeChange = (
    key: 'rentableSquareFeet' | 'rentPerSquareFoot',
    bound: 'min' | 'max',
    input: string,
  ): void => {
    const raw = input.trim();
    onFiltersChange((current) => ({
      ...current,
      [key]: {
        ...current[key],
        [bound]: raw.length === 0 ? null : Number(raw),
      },
    }));
  };

  const handleDivisibleChange = (value: boolean | null): void => {
    onFiltersChange((current) => ({
      ...current,
      potentiallyDivisible: value,
    }));
  };

  const activeFilterLabel = hasActiveFilters
    ? `${activeFilterCount.toString()} active filter${activeFilterCount === 1 ? '' : 's'}`
    : 'No active filters';

  return (
    <section className="flex flex-col gap-5 rounded-3xl border border-surface-subtle bg-white px-4 py-5 shadow-sm">
      {showHeader ? (
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
              Filters
            </p>
            <p className="text-sm text-text-primary/80">{activeFilterLabel}</p>
          </div>
          <button
            type="button"
            className="text-sm font-semibold text-brand-primary transition hover:text-brand-primary/80 disabled:text-text-primary/40"
            onClick={onReset}
            disabled={!hasActiveFilters}
          >
            Reset
          </button>
        </header>
      ) : null}

      <FilterSection title="Submarket">
        {normalizedSubmarkets.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {normalizedSubmarkets.map((option) => (
              <FilterChip
                key={option}
                label={option}
                selected={filters.submarkets.includes(option)}
                onToggle={() => {
                  handleSubmarketToggle(option);
                }}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-primary/60">
            Submarkets load automatically from active inventory.
          </p>
        )}
      </FilterSection>

      <FilterSection title="Asset type">
        <div className="flex flex-wrap gap-2">
          {assetTypeOptions.map((option) => (
            <FilterChip
              key={option}
              label={PUBLIC_ASSET_TYPE_LABELS[option]}
              selected={filters.assetTypes.includes(option)}
              onToggle={() => {
                handleAssetTypeToggle(option);
              }}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Rentable square feet">
        <RangeInputRow>
          <RangeInput
            id="dashboard-rentable-min"
            label="Min"
            suffix="SF"
            value={toNumberInputValue(filters.rentableSquareFeet.min)}
            onChange={(event) => {
              handleRangeChange('rentableSquareFeet', 'min', event.target.value);
            }}
          />
          <RangeInput
            id="dashboard-rentable-max"
            label="Max"
            suffix="SF"
            value={toNumberInputValue(filters.rentableSquareFeet.max)}
            onChange={(event) => {
              handleRangeChange('rentableSquareFeet', 'max', event.target.value);
            }}
          />
        </RangeInputRow>
      </FilterSection>

      <FilterSection title="Net rent per SF">
        <RangeInputRow>
          <RangeInput
            id="dashboard-rent-min"
            label="Min"
            prefix="$"
            step="0.5"
            value={toNumberInputValue(filters.rentPerSquareFoot.min)}
            onChange={(event) => {
              handleRangeChange('rentPerSquareFoot', 'min', event.target.value);
            }}
          />
          <RangeInput
            id="dashboard-rent-max"
            label="Max"
            prefix="$"
            step="0.5"
            value={toNumberInputValue(filters.rentPerSquareFoot.max)}
            onChange={(event) => {
              handleRangeChange('rentPerSquareFoot', 'max', event.target.value);
            }}
          />
        </RangeInputRow>
      </FilterSection>

      <FilterSection title="Readiness">
        <div className="flex flex-wrap gap-2">
          {PUBLIC_READINESS_ORDER.map((option) => (
            <FilterChip
              key={option}
              label={PUBLIC_READINESS_LABELS[option]}
              selected={filters.readinessStates.includes(option)}
              onToggle={() => {
                handleReadinessToggle(option);
              }}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Programs">
        <label className="flex items-center gap-3 rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm font-medium text-text-primary">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
            checked={filters.incentivesOnly}
            onChange={(event) => {
              onFiltersChange((current) => ({
                ...current,
                incentivesOnly: event.target.checked,
              }));
            }}
          />
          Show incentives only
        </label>
      </FilterSection>

      <FilterSection title="Divisibility">
        <div className="grid grid-cols-3 gap-2">
          <SegmentedButton
            label="Any"
            selected={filters.potentiallyDivisible === null}
            onClick={() => {
              handleDivisibleChange(null);
            }}
          />
          <SegmentedButton
            label="Divisible"
            selected={filters.potentiallyDivisible === true}
            onClick={() => {
              handleDivisibleChange(true);
            }}
          />
          <SegmentedButton
            label="Single"
            selected={filters.potentiallyDivisible === false}
            onClick={() => {
              handleDivisibleChange(false);
            }}
          />
        </div>
      </FilterSection>

      <FilterSection title="Vacancy">
        <label className="flex items-center gap-3 rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm font-medium text-text-primary">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
            checked={filters.hasVacancy}
            onChange={(event) => {
              onFiltersChange((current) => ({
                ...current,
                hasVacancy: event.target.checked,
              }));
            }}
          />
          Only show properties with published vacancies
        </label>
      </FilterSection>
    </section>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function FilterChip({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={clsx(
        'inline-flex min-h-11 items-center justify-center rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40',
        selected
          ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
          : 'border-surface-subtle bg-white text-brand-primary hover:border-brand-primary/40 hover:bg-brand-primary/10',
      )}
    >
      {label}
    </button>
  );
}

function RangeInputRow({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

function RangeInput({
  id,
  label,
  prefix,
  suffix,
  step,
  value,
  onChange,
}: {
  id: string;
  label: string;
  prefix?: string;
  suffix?: string;
  step?: string;
  value: number | '';
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}): JSX.Element {
  return (
    <label
      htmlFor={id}
      className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-wide text-text-primary/60"
    >
      {label}
      <div className="flex items-center gap-2 rounded-2xl border border-surface-subtle bg-white px-3 py-2.5 md:py-2">
        {typeof prefix === 'string' && prefix.length > 0 ? (
          <span className="text-text-primary/70">{prefix}</span>
        ) : null}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          min="0"
          value={value}
          onChange={onChange}
          className="w-full border-none bg-transparent text-base font-semibold text-brand-primary outline-none md:text-sm"
        />
        {typeof suffix === 'string' && suffix.length > 0 ? (
          <span className="text-text-primary/70">{suffix}</span>
        ) : null}
      </div>
    </label>
  );
}

function SegmentedButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        'min-h-11 rounded-2xl border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40',
        selected
          ? 'border-brand-primary bg-brand-primary text-white'
          : 'border-surface-subtle bg-white text-brand-primary hover:border-brand-primary/40 hover:bg-brand-primary/5',
      )}
    >
      {label}
    </button>
  );
}

function toggleString(values: string[], value: string): string[] {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }
  return [...values, value];
}

function toggleEnum<T extends string>(values: T[], value: T): T[] {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }
  return [...values, value];
}

function toNumberInputValue(value?: number | null): number | '' {
  return typeof value === 'number' ? value : '';
}
